import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession } from '../services/payment';
import { logError } from '../utils/logger';

// TODO: Update these display prices to match what you set in the Stripe Dashboard
const PLANS = [
  {
    id: 'monthly',
    price: '£3',
    period: '/mo',
  },
  {
    id: '90day',
    price: '£5',
    period: '',
  },
];

export default function SubscribePage() {
  const { t } = useTranslation();
  const { profile, isPremium, refreshProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const didSucceed = searchParams.get('success') === 'true';
  const didCancel = searchParams.get('cancelled') === 'true';

  // Refresh the profile when returning from a successful payment so isPremium
  // updates immediately (the webhook has usually fired by the time the user lands here).
  useEffect(() => {
    if (didSucceed) refreshProfile();
  }, [didSucceed]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubscribe(planType) {
    setError('');
    setLoading(planType);
    try {
      const successUrl = `${window.location.origin}/subscribe?success=true`;
      const cancelUrl = `${window.location.origin}/subscribe?cancelled=true`;
      const { url } = await createCheckoutSession(planType, successUrl, cancelUrl);
      window.location.href = url;
    } catch (err) {
      logError(err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(null);
    }
  }

  const sub = profile?.subscription;

  function formatExpiry(expiresAt) {
    if (!expiresAt) return null;
    const d = expiresAt?.toDate ? expiresAt.toDate() : new Date(expiresAt);
    return d.toLocaleDateString(undefined, { dateStyle: 'long' });
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold t-text text-center mb-2">{t('subscribe.heading')}</h1>
        <p className="text-center t-text-muted mb-8">
          {t('subscribe.subheading')}
        </p>

        {didSucceed && (
          <div className="mb-6 p-4 rounded-lg t-bg-success t-border-success border t-text-success text-center font-medium">
            {t('subscribe.successMessage')}
          </div>
        )}
        {didSucceed && (
          <div className="mb-6 text-center">
            <a
              href="/lobby"
              className="inline-block py-2.5 px-6 rounded-lg t-btn-primary font-semibold transition-colors t-focus-ring"
            >
              {t('subscribe.returnToLobby')}
            </a>
          </div>
        )}
        {didCancel && (
          <div className="mb-6 p-4 rounded-lg t-bg-warning t-border-warning border t-text-warning text-center">
            {t('subscribe.cancelledMessage')}
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 rounded-lg t-bg-error t-border-error border t-text-error text-center">
            {error}
          </div>
        )}

        {isPremium ? (
          <div className="text-center p-8 rounded-xl border-2 t-border-success t-bg-success">
            <div className="text-3xl mb-3 t-text-success">{t('subscribe.youHavePremium')}</div>
            {sub?.type === '90day' && sub?.expiresAt && (
              <p className="t-text-muted">
                {t('subscribe.expiresOn', { date: formatExpiry(sub.expiresAt) })}
              </p>
            )}
            {sub?.type === 'monthly' && (
              <p className="t-text-muted">{t('subscribe.monthlyActive')}</p>
            )}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {PLANS.map((plan) => {
              const planPerks = [
                t(`subscribe.plans.${plan.id}.perk1`),
                t(`subscribe.plans.${plan.id}.perk2`),
              ];
              return (
                <div
                  key={plan.id}
                  className="t-bg-surface t-border-accent border-2 rounded-xl p-6 flex flex-col"
                >
                  <h2 className="text-xl font-bold t-text mb-1">{t(`subscribe.plans.${plan.id}.name`)}</h2>
                  <p className="t-text-muted text-sm mb-4">{t(`subscribe.plans.${plan.id}.tagline`)}</p>
                  <div className="text-3xl font-bold t-text mb-6">
                    {plan.price}
                    {plan.period && (
                      <span className="text-lg font-normal t-text-muted">{plan.period}</span>
                    )}
                  </div>
                  <ul className="text-sm t-text-secondary space-y-2 mb-6 flex-1">
                    {planPerks.map((p) => (
                      <li key={p}>✓ {p}</li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={!!loading}
                    className="w-full py-2.5 px-4 rounded-lg t-btn-primary font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed t-focus-ring"
                  >
                    {loading === plan.id
                      ? t('subscribe.redirectingToStripe')
                      : t(`subscribe.plans.${plan.id}.button`)}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
